import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";

import { DeskSuggestionReview } from "@/components/desk-suggestions/desk-suggestion-review";
import { RunStatusBadge } from "@/components/desk-suggestions/run-status-badge";
import { RunsTable } from "@/components/desk-suggestions/runs-table";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { useDesks } from "@/hooks/use-desk-migration";
import {
  useDeskSuggestionRun,
  useDeskSuggestionRuns,
} from "@/hooks/use-desk-suggestions";
import { useAllTenants } from "@/hooks/use-tenants";
import { CellTenantsUnavailable } from "@/components/cells/cell-failures-alert";
import { tenantLabel, tenantOptionValue } from "@/lib/cells/cells";
import { formatDateTime } from "@/lib/date-utils";
import { cn } from "@/lib/utils";

function Spinner({ label }: { label: string }) {
  return (
    <div role="status" aria-label={label}>
      <Loader2
        aria-hidden
        className="text-muted-foreground h-5 w-5 animate-spin"
      />
    </div>
  );
}

export const Route = createFileRoute("/desk-suggestions")({
  component: DeskSuggestionsPage,
});

function DeskSuggestionsPage() {
  const [tenantId, setTenantId] = useState("");
  const [runId, setRunId] = useState("");
  const [tenantPopoverOpen, setTenantPopoverOpen] = useState(false);

  const {
    data: tenants,
    isLoading: tenantsLoading,
    failures: tenantFailures,
  } = useAllTenants();
  const {
    data: runs,
    isLoading: runsLoading,
    error: runsError,
  } = useDeskSuggestionRuns(tenantId);
  const { data: run, error: runError } = useDeskSuggestionRun(tenantId, runId);
  const {
    data: desks,
    isLoading: desksLoading,
    isError: desksError,
  } = useDesks(tenantId);

  const sortedTenants = useMemo(
    () =>
      [...(tenants ?? [])].sort((a, b) => a.company.localeCompare(b.company)),
    [tenants],
  );
  const selectedTenant = tenants?.find((t) => t.id === tenantId);

  const handleTenantChange = (id: string) => {
    setTenantId(id);
    setRunId("");
    setTenantPopoverOpen(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Desk Suggestions</h1>
        <p className="text-muted-foreground">
          Review the desks the AI desk designer suggested for a tenant, and
          create the ones you want.
        </p>
      </div>
      <Separator />

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Select Tenant</CardTitle>
            <CardDescription>
              Choose the tenant whose desk suggestions you want to review.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Popover
              open={tenantPopoverOpen}
              onOpenChange={setTenantPopoverOpen}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={tenantPopoverOpen}
                  className="w-[300px] justify-between"
                >
                  <span className="truncate">
                    {tenantsLoading
                      ? "Loading..."
                      : selectedTenant
                        ? tenantLabel(selectedTenant)
                        : "Select tenant"}
                  </span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[300px] p-0" align="start">
                <Command>
                  <CellTenantsUnavailable failures={tenantFailures} />
                  <CommandInput placeholder="Search tenants..." />
                  <CommandList className="max-h-[300px]">
                    <CommandEmpty>No tenant found.</CommandEmpty>
                    <CommandGroup>
                      {sortedTenants.map((tenant) => (
                        <CommandItem
                          key={tenant.id}
                          value={tenantOptionValue(tenant)}
                          onSelect={() => {
                            handleTenantChange(tenant.id);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              tenantId === tenant.id
                                ? "opacity-100"
                                : "opacity-0",
                            )}
                          />
                          {tenantLabel(tenant)}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </CardContent>
        </Card>

        {tenantId && (
          <Card>
            <CardHeader>
              <CardTitle>Suggestion Runs</CardTitle>
              <CardDescription>
                Newest first. Pick a run to review its desks.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {runsLoading ? (
                <Spinner label="Loading desk suggestion runs" />
              ) : runsError && !runs ? (
                <p className="text-destructive text-sm">
                  Failed to load desk suggestion runs.
                </p>
              ) : runs && runs.length > 0 ? (
                <RunsTable
                  runs={runs}
                  selectedRunId={runId}
                  onSelect={setRunId}
                />
              ) : (
                <p className="text-muted-foreground text-sm">
                  No desk suggestion runs for this tenant.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {tenantId && runId && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Suggested Desks {run && <RunStatusBadge status={run.status} />}
              </CardTitle>
              {run && (
                <CardDescription>
                  Created {formatDateTime(run.created_at)}
                  {run.applied_at &&
                    ` · last applied ${formatDateTime(run.applied_at)}`}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              {!run ? (
                runError ? (
                  <p className="text-destructive text-sm">
                    Failed to load the desk suggestion run.
                  </p>
                ) : (
                  <Spinner label="Loading desk suggestion run" />
                )
              ) : run.status === "failed" ? (
                <p className="text-destructive text-sm">
                  This run failed{run.error ? `: ${run.error}` : "."}
                </p>
              ) : run.status === "pending" || run.status === "running" ? (
                <div
                  role="status"
                  className="text-muted-foreground flex items-center gap-2 text-sm"
                >
                  <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
                  The AI desk designer is still generating suggestions. This
                  page updates when it finishes.
                </div>
              ) : (
                <DeskSuggestionReview
                  key={run.id}
                  tenantId={tenantId}
                  run={run}
                  existingDesks={desks ?? []}
                  existingDesksLoading={desksLoading}
                  existingDesksError={desksError && !desks}
                />
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
